/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { Consts, I18nService, TimeService, UNPLACEHOLDER, UtilService } from '@dm/core';
import { type SelectOption } from '@dm/core/types';

import { ToolService } from '../../../../../common/services';
import { BackupCopyPolicyType, BandwidthRange, RemTimeoutPeriodRange, SyncSnapPolicyType } from './../../lun/rep-pair/rep-pair-enum';

interface KV { [key: string]: string }

@Injectable({
    providedIn: 'root',
})
export class ReplicationStrService {
    constructor(
        private i18n: I18nService,
        private util: UtilService,
        private time: TimeService,
        private toolService: ToolService,
    ) { }
    /**
     * i18n冒号
     */
    colon = this.i18n.get('common.colon_label');
    get = this.i18n.get;
    i18nLabel = {
        vstoreLabel: this.get('common.owningVstore_lab'),
        rmVstoreLabel: this.get('common.remoteVstore_lab'),
        vstoreName: this.get('vstore_name_lab'),
        vstoreId: this.get('vstore_id_lab'),
        remoteVstoreId: this.get('common.remoteVstoreID_column'),
        repNameErr: this.i18n.get('protection.HyperMetroDomain_task_create_name_valid'),
        yesLabel: this.i18n.get('common.term_yes_label'),
        isStandbyLabel: this.i18n.get('protection.dataProtection_term_drStar_standby_lab'),
        owningDrStar: this.i18n.get('protection.dataProtection_term_drStar_ID_lab'),
        pairListLab: this.i18n.get('protection.replicationCG_title'),
        autoRescoveryStrategyTips: this.i18n.get('protection.replicaiton_create_setProperty_option_rescoveryStrategy_auto_des_lab'),
        valueRange: this.i18n.get('common.valueRange_label'),
        manuRescoveryStrategyTips: this.i18n.get('protection.replicaiton_create_setProperty_option_rescoveryStrategy_manual_des_lab'),
        syncMethodTips: this.i18n.get('protection.replication_create_task_para_SynchronizateType_SpecificTimePolicy_tip'),
        linkCompressionTips: this.i18n.get('protection.replication_task_create_info_linkCompression_tip'),

        // eslint-disable-next-line max-len
        autoInitalSyncTips: this.i18n.get('protection.replicaiton_create_task3_setProperty_option_initSync_CG_value_inconsist_auto_message'),
        manuInitalTips: this.i18n.get('protection.replicaiton_create_task3_setProperty_option_initSync_CG_value_inconsist_manual_message'),
        noInitalSyncAutoTips: this.i18n.get('protection.replicaiton_create_task3_setProperty_option_initSync_value_consist_message'),

        customSpeedUnit: this.i18n.get('common.unit_MBS'),
        bandwidthRangeLabel: this.i18n.get('common.valueRange_label', [BandwidthRange.Min, BandwidthRange.Max]),
        timeoutintervalRange: this.i18n.get('common.valueRange_label', ['10', '30']),

        deviceModel: this.i18n.get('common.device_model_label'),
        createBtn: this.i18n.get('common.create_button'),
        deleteBtn: this.i18n.get('common.delete_button'),
        closeBtn: this.i18n.get('common.close_button'),
        addBtn: this.i18n.get('common.add_button'),
        okBtn: this.i18n.get('common.confirm_button'),
        cancelBtn: this.i18n.get('common.cancel_button'),
        removeBtn: this.i18n.get('common.remove_button'),
        modifyBtn: this.i18n.get('common.modify_button'),
        actionBtn: this.i18n.get('common.operation_button'),
        advance: this.i18n.get('common.advance_label'),

        name: this.i18n.get('common.name_column'),
        description: this.i18n.get('common.description_column'),
        status: this.i18n.get('common.status_column'),
        sn: this.i18n.get('common.device_ESN_label'),
        pleaseSelect: this.i18n.get('common.select_lab'),

        replicationCG: this.i18n.get('protection.replicationCG_title'),

        rangeResult: (value1, value2): string => this.i18n.get('common.valueRange_label', [value1, value2]),

        synchronize: this.i18n.get('protection.replicationCG_sync_button'),
        split: this.i18n.get('protection.replicationCG_split_button'),
        switchover: this.i18n.get('protection.replicationCG_switch_button'),
        switchoverTitle: this.i18n.get('protection.replicationCG_switch_tittle'),
        enableSecondary: this.i18n.get('protection.replicationCG_term_enableSecondResourceProtect_button'),
        disableSecondary: this.i18n.get('protection.replicationCG_term_disableSecondResourceProtect_button'),
        enableSecondSourceTip: this.i18n.get('common.replicationPair_task_enableSecondSource_error_tips'),
        disableSecondSourceTip: this.i18n.get('common.replicationPair_task_disableSecondSource_error_tips'),
        pairHealthStatus: this.i18n.get('protection.hyperMetroCG_term_pairHealthStatus_column'),
        pairRrunningStatus: this.i18n.get('protection.hyperMetroCG_term_pairRunningStatus_column'),
        healthStatus: this.i18n.get('common.healthStatus_column'),
        runningStatus: this.i18n.get('common.runningStatus_column'),
        protectionGroup: this.i18n.get('protection.protectionGroup_task_create_protectionGroup_lab'),
        targetGroup: this.i18n.get('protection.replicationCG_term_targetGroup_lab'),
        localPG: this.i18n.get('protection.replicationCG_term_localPG_lab'),
        remotePG: this.i18n.get('protection.replicationCG_term_remotePG_lab'),
        remotePgAndDeviceLab: this.i18n.get('protection.replicationCG_term_remotePGDevice_lab'),
        remotePgSearchPlaceholder: this.i18n.get('protection.replicationCG_term_searchRemotePG_lab'),
        remoteDeviceSN: this.i18n.get('protection.replicationCG_term_remoteDeviceSN_column'),
        role: this.i18n.get('protection.hyperMetroCG_term_localSourceRole_column'),
        replicationModel: this.i18n.get('common.copyMode_label'),
        pairSpeedLab: this.i18n.get('protection.HyperMetroCG_term_pairSpeed_column'),
        speedTip: this.i18n.get('protection.replication_create_task_speed_tip'),
        synchronizationMethod: this.i18n.get('protection.replicationCG_term_syncMode_label'),
        interval: this.i18n.get('protection.replicationCG_term_timingPeriod_label'),
        recoveryPolicy: this.i18n.get('protection.replicationCG_term_rescoveryStrategy_lab'),
        linkCompression: this.i18n.get('protection.replicationCG_term_linkCompression_lab'),
        compressionStatus: this.i18n.get('protection.configuration_remoteDevice_task_modify_compressStatus_label'),
        syncStartTime: this.i18n.get('protection.replicationCG_term_syncStartTime_label'),
        syncEndTime: this.i18n.get('protection.replicationCG_term_syncEndTime_label'),
        descriptionLab: this.i18n.get('common.description_column'),
        localLunName: this.i18n.get('common.localLunName_column'),
        remoteLunName: this.i18n.get('common.remoteLunName_column'),
        localResource: this.i18n.get('common.localResource_column'),
        remoteResource: this.i18n.get('common.remoteResource_column'),
        secResDataStatus: this.i18n.get('protection.replicationCG_task_view_secondaryLunDataStatus_column'),
        secResProtectStatus: this.i18n.get('protection.replicationCG_task_view_secondayResourceProtectionStauts_lab'),
        timeDiff: this.i18n.get('protection.replicationCG_task_view_timeDifference_lab'),
        remTimeoutPeriodRangeLabel: this.i18n.get('common.valueRange_label', [RemTimeoutPeriodRange.Min, RemTimeoutPeriodRange.Max]),


        createReplicationCG: this.i18n.get('protection.replicationCG_task_create_title'),
        Asynchronous: this.i18n.get('common.EnumDefine_RM_MODEL_E_RM_MODEL_ASYNC'),
        Synchronous: this.i18n.get('common.EnumDefine_RM_MODEL_E_RM_MODEL_SYNC'),
        replicationModelSyncTip: this.get('protection.replicaiton_task_create_Pair_syncMode_info'),
        localDevice: this.i18n.get('common.EnumDefine_PRIORITY_STATION_TYPE_E_LOCAL'),
        remoteDevice: this.i18n.get('common.remoteDevice_title'),
        pairCreationMode: this.i18n.get('protection.replicationCG_task_create_pairCreateMode_lab'),
        Automatic: this.i18n.get('common.EnumDefine_STARTUP_MODE_E_AUTO'),
        Manual: this.i18n.get('common.EnumDefine_STARTUP_MODE_E_MANUAL'),
        targetStoragePool: this.i18n.get('protection.replicationCG_task_create_targetPool_lab'),
        remoteStoragepool: this.i18n.get('protection.replicaiton_task_create_remoteStoragePool_lab'),
        replicationPairs: this.i18n.get('protection.replicationCG_task_create_remoteRepliPair_lab'),
        replicationPairsLab: this.i18n.get('common.RemoteReplicationPairs'),
        configure: this.i18n.get('protection.replicationCG_task_create_remoteRepliPair_configure_lab'),
        syncMethod: this.i18n.get('protection.replicationCG_term_syncMode_label'),
        dataTransmission: this.i18n.get('storage.lun_task_create_smartCompression_label'),
        initialSync: this.i18n.get('protection.replicationCG_task_create_initialSync_lab'),
        setTimeOutTitle: this.i18n.get('protection.replicaiton_task_view_remoteIOTimeout_lab'),
        setTimeOutLable: this.i18n.get('protection.replicationCG_task_create_remoteIOTimeout_lab'),
        setTimeOutTip: this.i18n.get('protection.replicationCG_task_create_remoteIOTimeout_desc'),
        timeoutInterval: this.i18n.get('protection.replicationCG_task_create_Timeout_lab'),
        inconsistent: this.i18n.get('protection.hyperMetroCG_task_create_initialSync_dataInConsistent_checkbox'),
        consistent: this.i18n.get('protection.hyperMetroCG_task_create_initialSync_dataConsistent_checkbox'),
        notSync: this.i18n.get('protection.hyperMetroCG_task_create_initialSync_dataConsistent_noSync_value'),

        customSpeed: this.i18n.get('protection.replicationCG_task_create_custom_speed_lab'),

        confiurePair: this.i18n.get('protection.protectionGroup_task_addMember_configPair_lab'),
        sourceLun: this.i18n.get('common.sourceLun_label'),
        targetLun: this.i18n.get('common.targetLun_label'),
        operation: this.i18n.get('common.operation_button'),
        capacity: this.i18n.get('common.capacity_column'),
        addToPairList: this.i18n.get('protection.replicationCG_task_configPair_addPairList_button'),
        deleteOption: this.i18n.get('protection.replicationCG_task_delete_confirm_deleteRemoteCG_checkbox'),
        pairsErrMsg: this.i18n.get('protection.replicationCG_task_configPair_tip'),
        autoPairTips: this.i18n.get('protection.replicationCG_task_create_pairMode_auto_lab'),
        manuPairTips: this.i18n.get('protection.replicationCG_task_create_pairMode_manua_lab'),

        syncReCG: this.i18n.get('protection.replicationCG_synRemoteRepCG_title'),
        splitReCG: this.i18n.get('protection.replicationCG_splitRemoteRepCG_title'),
        deleteReCG: this.i18n.get('protection.replicationCG_deleteRemoteRepCG_title'),
        modifyTitle: this.i18n.get('protection.replicationCG_task_modify_title'),

        min: this.i18n.get('common.time_minutes_label'),
        sec: this.i18n.get('common.time_seconds_label'),
        loading: this.i18n.get('common.loading_label'),
        more: this.i18n.get('common.more_button'),
        noData: this.i18n.get('monitor.recentTasks_result_noData'),
        // 按时间策略
        timeStrategy: this.i18n.get('common.EnumDefine_RM_UPDATE_TYPE_E_RM_UPDATE_TYPE_SEPCIFY_TIMING'),
        sun: this.i18n.get('common.seven_checkbox'),
        mon: this.i18n.get('common.one_checkbox'),
        tues: this.i18n.get('common.two_checkbox'),
        wed: this.i18n.get('common.three_checkbox'),
        thrs: this.i18n.get('common.four_checkbox'),
        fri: this.i18n.get('common.five_checkbox'),
        sat: this.i18n.get('common.six_checkbox'),
        specifyTimeErr: this.i18n.get('protection.HyperCDPSchedule_task_create_step2_weeklyExecute_tip'),
        time: this.i18n.get('common.time_label'),
        weekLabel: this.i18n.get('common.week_label'),
        // 时间策略
        timeStrategys: this.i18n.get('protection.replication_task_configPair_timePolicy_lab'),
        week: this.i18n.get('protection.replication_task_configPair_week_lab'),
        hour: this.i18n.get('protection.replication_task_configPair_Hourly_lab'),
        day: this.i18n.get('protection.replication_task_configPair_Daily_lab'),
        minute: this.i18n.get('protection.replication_task_configPair_Minute_lab'),
        syncReCgResult: (params): string => this.i18n.get('rest.syncReplicationCG_msg1', [params]),
        splitReCgResult: (params): string => this.i18n.get('rest.splitReplicationCG_msg1', [params]),
        deleteReCgResult: (params): string => this.i18n.get('rest.deleteReplicationCG_msg1', [params]),
        intervalRange: (minimum, maximum): string => this.i18n.get('common.valueRange_label', [minimum, maximum]),
        // '每周{0}{1}执行'
        repPairWeekDescLab: this.i18n.get('protection.replication_task_configPair_weekDesc_lab'),
        // '每小时的第{0}分钟执行'
        repPairHourlyDescLab: this.i18n.get('protection.replication_task_configPair_HourlyDesc_lab'),
        // '每天{0}执行'
        repPairDailyDescLab: this.i18n.get('protection.replication_task_configPair_DailyDesc_lab'),

        commonLimitLabel: (min, max): string => this.i18n.get('common.common_limit_label', [min, max]),
        bandwidthLabel: this.i18n.get('common.bandwidth_label'),
        bandwidthLabelLeft: this.i18n.get('common.bandwidth_label') + ' > ',
        bandwidthLabelRight: this.i18n.get('common.bandwidth_label') + ' < ',
        durationLabel: this.i18n.get('protection.replicaiton_task_modify_duration_lab'),
        unconfiguredLab: this.i18n.get('common.unconfigured_lab'),
        rangeValidation: (label, min, max): string => this.i18n.get('common.range_validation', [label, min, max]),
        performanceTermDelayLab: this.i18n.get('home.performance_term_delay_lab'),
        performanceTermDelayLabLeft: this.i18n.get('home.performance_term_delay_lab') + ' > ',
        performanceTermDelayLabRight: this.i18n.get('home.performance_term_delay_lab') + ' < ',
        whenLab: this.i18n.get('common.when_lab'),
        orLab: this.i18n.get('common.or_lab'),
        andLab: this.i18n.get('common.and_lab'),
        asyncSwitchBtn: this.i18n.get('protection.replicaiton_task_manualSyncAsyncSwitch_btn'),
        asyncRepLab: this.i18n.get('protection.replicaiton_task_modify_startAsyncRep_lab'),
        syncRepLab: this.i18n.get('protection.replicaiton_task_modify_startSyncRep_lab'),
        configAtLeastOneErrorInfoLab: this.i18n.get('protection.replicaiton_task_modify_configAtLeastOne_error_info_lab'),
        bandwidthLargerErrorInfoLab: this.i18n.get('protection.replicaiton_task_modify_bandwidth_asyncLarger_error_info_lab'),
        performanceTermDelayLargerErrorInfoLab: this.i18n.get('protection.replicaiton_task_modify_latency_asyncLarger_error_info_lab'),
        durationErrorInfoLab: this.i18n.get('protection.replicaiton_task_modify_duration_error_info_lab'),
        // detail
        asyncRepCondition1Lab: (bandwitdh): string => this.i18n.get('protection.replicaiton_task_view_startAsyncRep_condition1_lab', [bandwitdh]),
        asyncRepCondition2Lab: (latency): string => this.i18n.get('protection.replicaiton_task_view_startAsyncRep_condition2_lab', [latency]),
        asyncRepCondition3Lab: (bandwitdh, latency): string => this.i18n.get('protection.replicaiton_task_view_startAsyncRep_condition3_lab',
            [bandwitdh, latency]),

        syncRepCondition1Lab: (bandwitdh, transferCycle): string => this.i18n.get('protection.replicaiton_task_view_startSyncRep_condition1_lab',
            [bandwitdh, transferCycle]),
        syncRepCondition2Lab: (latency, transferCycle): string => this.i18n.get('protection.replicaiton_task_view_startSyncRep_condition2_lab',
            [latency, transferCycle]),
        // eslint-disable-next-line max-len
        syncRepCondition3Lab: (bandwitdh, latency, transferCycle): string => this.i18n.get('protection.replicaiton_task_view_startSyncRep_condition3_lab',
            [bandwitdh, latency, transferCycle]),
        syncRepCondition4Lab: (bandwitdh): string => this.i18n.get('protection.replicaiton_task_view_startSyncRep_condition4_lab',
            [bandwitdh]),
        syncRepCondition5Lab: (latency): string => this.i18n.get('protection.replicaiton_task_view_startSyncRep_condition5_lab',
        [latency]),
        syncRepCondition6Lab: (bandwitdh, latency): string => this.i18n.get('protection.replicaiton_task_view_startSyncRep_condition6_lab',
            [bandwitdh, latency]),
        // card
        cardAsyncRep_condition1Lab: (bandwitdh): string => this.i18n.get('protection.replicaiton_task_view_startAsyncRep_condition1_info',
            [bandwitdh]),
        cardAsyncRep_condition2Lab: (latency): string => this.i18n.get('protection.replicaiton_task_view_startAsyncRep_condition2_info', [latency]),
        cardAsyncRep_condition3Lab: (bandwitdh, latency): string => this.i18n.get('protection.replicaiton_task_view_startAsyncRep_condition3_info',
            [bandwitdh, latency]),
        cardSyncRep_condition1Lab: (bandwitdh, transferCycle): string =>
            this.i18n.get('protection.replicaiton_task_view_startSyncRep_condition1_info', [bandwitdh, transferCycle]),
        cardSyncRep_condition2Lab: (latency, transferCycle): string =>
            this.i18n.get('protection.replicaiton_task_view_startSyncRep_condition2_info', [latency, transferCycle]),
        cardSyncRep_condition3Lab: (bandwitdh, latency, transferCycle): string =>
            this.i18n.get('protection.replicaiton_task_view_startSyncRep_condition3_info', [bandwitdh, latency, transferCycle]),
        cardSyncRep_condition4Lab: (bandwitdh): string => this.i18n.get('protection.replicaiton_task_view_startSyncRep_condition4_info',
            [bandwitdh]),
        cardSyncRep_condition5Lab: (latency): string => this.i18n.get('protection.replicaiton_task_view_startSyncRep_condition5_info', [latency]),
        cardSyncRep_condition6Lab: (bandwitdh, latency): string => this.i18n.get('protection.replicaiton_task_view_startSyncRep_condition6_info',
            [bandwitdh, latency]),
        asyncSwitchErrorTip: this.i18n.get('protection.replicaiton_task_modify_manualSyncAsyncSwitch_error_tip'),
        timeAcountMinutesLabel: this.i18n.get('common.timeAcount_minutes_label'),
        msLabel: this.i18n.get('common.ms_label'),
        advancedLab: this.i18n.get('common.advance_label'),
        disableValue: this.i18n.get('common.service_disable_value'),
        switchLab: this.i18n.get('protection.replicaiton_task_modify_enableAutoSyncAsyncSwitch_error_tip'),
        startAsyncRepLab: this.i18n.get('protection.replicaiton_task_modify_startAsyncRep_error_tip'),
        valueRangeLabel: (min, max): string => this.i18n.get('common.valueRange_label', [min, max]),
        switchRepModeCGMsg: this.i18n.get('rest.switchRepModeCG_msg'),
        manualStartAsyncRepLab: this.i18n.get('protection.replicaiton_task_modify_manualStartAsyncRep_lab'),
        syncSnapPolicyLab: this.i18n.get('protection.replicaiton_task_createAsync_userSnapSync_lab'),
        userSnapRetentionNumLab: this.i18n.get('protection.replicaiton_task_createAsync_secondUserSnapRetenNum_lab'),
        backupCopyPolicyLab: this.i18n.get('protection.replicaiton_task_createAsync_secondSnapCopyReten_lab'),
        copySnapRetentionNumLab: this.i18n.get('protection.replicaiton_task_createAsync_secondSnapCopyRetenNum_lab'),
        syncSnapPolicyTip: this.i18n.get('protection.replicaiton_task_createAsync_userSnapSync_CG_tip'),
        backupCopyPolicyTip: this.i18n.get('protection.replicaiton_task_createAsync_secondSnapCopyReten_CG_tip'),
        remoteDeviceIsCompatibiltyErr: this.i18n.get('protection.protection_task_v3_supportAsync_info'),
        supportManualInfo: this.i18n.get('protection.protection_task_v3_supportManual_info'),
        supportManualOnlyInfo: this.i18n.get('protection.protection_task_v3_supportManualOnly_info'),
        atLeastSelectOneTip: this.i18n.get('common.selectOneObject_lab'),
        getAtLeastSelectMaxTip: (value): string => this.i18n.get('protection.replicationCG_task_addPair_upLimit_info', [value]),
        addPairLab: this.i18n.get('protection.replicationCG_task_addPair_title'),
        removePairLab: this.i18n.get('protection.replicationCG_task_removePair_title'),
        leftGroupTitle: this.i18n.get('protection.replicationCG_task_availPairs_title'),
        rightGroupTitle: this.i18n.get('protection.replicationCG_task_selectedPairs_title'),
        addPairInfo: this.i18n.get('protection.replicationCG_task_addPair_info_lab'),
        compatibleErr: this.i18n.get('protection.protection_task_v3_notSupport_info'),
        splitRepPair: this.i18n.get('rest.splitReplicationPair_msg'),
        pairArrowRight: '➜',

        sameAsLocalValue: this.i18n.get('protection.replication_term_sameAsLocal_value'),
        customValue: this.i18n.get('common.custom_value'),
        prefixMsg: this.i18n.get('protection.replication_term_prefix_validation'),
        suffixMsg: this.i18n.get('protection.replication_term_suffix_validation'),
        prefixTip: this.i18n.get('protection.replication_term_prefix_tip'),
        suffixTip: this.i18n.get('protection.replication_term_suffix_tip'),
        selectOneAtLeastMsg: this.i18n.get('protection.replication_term_selectOneAtLeast_validation'),
        localLUNnameLabel: this.i18n.get('protection.replication_term_localLUNname_label'),

        // 远端资源名称
        remoteResourceName: this.i18n.get('protection.replication_term_remoteResourceName_label'),
        remoteNameTip: this.get('protection.replication_term_remoteResourceName_tip', [255, 255]),
        // 本端资源名称
        localResourceName: this.i18n.get('protection.replication_term_localResourceName_label'),
        // 远端LUN组和远端保护组均采用该命名规则
        remotePGName: this.i18n.get('protection.replication_term_remotePGName_label'),
        // 自定义（{0}
        customShowInfo1: (namePrefix): string => this.i18n.get('protection.replication_term_customShow_info1', [namePrefix]),
        // *本端资源名称*
        customShowInfo2: this.i18n.get('protection.replication_term_customShow_info2'),
        // {1}）
        customShowInfo3: (nameSuffix): string => this.i18n.get('protection.replication_term_customShow_info3', [nameSuffix]),
        commonNameErr: this.i18n.get('common.nameformat_validation', [this.i18n.get('common.name_column')]),
        remoteResourceNameSame: this.i18n.get('protection.replication_term_remoteResourceNameSame_info_msg'),
        closeButton: this.i18n.get('common.close_button'),
        addPairErr: this.i18n.get('protection.replicationCG_task_addPair_msg'),
        preTaskFail: this.get('task_error_preTaskFail_message'),
        addPairErrMsg: this.i18n.get('protection.replicationCG_task_addPair_error_msg'),
        replicationCGAddLUNErrMsg: this.i18n.get('protection.replicationCG_task_addLUN_err_msg'),
        createBelongSameRemoteVstoreInfo: this.i18n.get('protection.replication_task_create_belongSameRemoteVstore_info'),
        addPairBelongSameRemoteVstoreInfo: this.i18n.get('protection.replicationCG_task_addPair_belongSameRemoteVstore_info'),
        nowSyncData: this.i18n.get('protection.replicaiton_task_view_toBeSyncDataNow_lab'),
        nextSyncData: this.i18n.get('protection.replicaiton_task_view_toBeSyncDataNext_lab'),
    };
    // 增加/移除pair最大值
    MAX_SELECTED_PAIR_NUM = 100;
    // 带宽范围
    bandwidthMin = '1 KB/s';
    bandwidthMax = '10 GB/s';
    bandwidthNumMin = 1;
    bandwidthNumMax = 10 * 1024 * 1024;
    // 时延范围
    performanceTermDelayMin = '1 ms';
    performanceTermDelayMax = '5000 ms';
    // 持续范围
    durationMin = 1;
    durationMax = 1440;
    // 带宽默认单位
    DEFAULT_BANDWIDTH_UNIT = 'KB/s';
    // 健康状态
    healthstatusE = {
        invalid: '14',
    };
    // 运行状态
    runningStatusE = {
        invalid: '35',
        standby: '110',
        Synchronizing: '23',
    };
    // 同步类型 “255”为无效值。 CG有pair沒有
    synchronizeTypeE = {
        invalid: '255',
    };

    statusClass = {
        1: 'green',
        2: 'red',
        14: 'gray',
    };

    healthStatusObj = this.getI18n({
        1: 'common.EnumDefine_STATUS_E_NORMAL',
        2: 'common.EnumDefine_HEALTH_STATUS_fault',
        14: 'common.EnumDefine_HEALTH_STATUS_E_INVALID',
    });

    deviceRunningStatusObj = this.getI18n({
        10: 'common.EnumDefine_RUNNING_STATUS_E_LINK_UP',
        11: 'common.EnumDefine_RUNNING_STATUS_E_LINK_DOWN',
        31: 'common.EnumDefine_RUNNING_STATUS_E_DISABLED',
        101: 'common.EnumDefine_RUNNING_STATUS_E_CONNECTING',
    });

    runningStatusObj = this.getI18n({
        1: 'common.EnumDefine_STATUS_E_NORMAL',
        23: 'common.EnumDefine_RUNNING_STATUS_E_SYNCHRONIZING',
        26: 'protection.replicationCG_split_button',
        33: 'common.EnumDefine_RUNNING_STATUS_E_TO_BE_RECOVERD',
        34: 'common.EnumDefine_RUNNING_STATUS_E_INTERRUPTED',
        35: 'common.EnumDefine_CMO_LICENSE_STATUS_4',
        110: 'common.EnumDefine_RUNNING_STATUS_E_STANDBY',
        118: 'common.EnumDefine_RUNNING_STATUS_E_AIRGAPDISCONNECT',
    });

    replicationModeObj = this.getI18n({
        1: 'common.EnumDefine_RM_MODEL_E_RM_MODEL_SYNC',
        2: 'common.EnumDefine_RM_MODEL_E_RM_MODEL_ASYNC',
    });

    syncMethodObj = this.getI18n({
        1: 'common.EnumDefine_STARTUP_MODE_E_MANUAL',
        2: 'common.EnumDefine_RM_UPDATE_TYPE_E_RM_UPDATE_TYPE_AUTO_START_TIMING',
        3: 'common.EnumDefine_RM_UPDATE_TYPE_E_RM_UPDATE_TYPE_AUTO_END_TIMING',
        4: 'common.EnumDefine_RM_UPDATE_TYPE_E_RM_UPDATE_TYPE_SEPCIFY_TIMING',
    });

    roleObj = this.getI18n({
        true: 'common.EnumDefine_ROLE_MASTER',
        false: 'common.EnumDefine_ROLE_SLAVER',
    });

    speedObj = this.getI18n({
        1: 'common.EnumDefine_SPEED_LEVEL_E_SPEED_LEVEL_LOW',
        2: 'common.EnumDefine_SPEED_LEVEL_E_SPEED_LEVEL_MIDDLE',
        3: 'common.EnumDefine_SPEED_LEVEL_E_SPEED_LEVEL_HIGH',
        4: 'common.EnumDefine_SPEED_LEVEL_E_SPEED_LEVEL_ASAP',
        custom: 'common.custom_value',
    });

    recoverPolicyObj = this.getI18n({
        1: 'common.EnumDefine_STARTUP_MODE_E_AUTO',
        2: 'common.EnumDefine_STARTUP_MODE_E_MANUAL',
    });

    dataTransmisionObj = this.getI18n({
        true: 'common.EnumDefine_REPLICATION_DATA_COMPRESSION_Y',
        false: 'common.EnumDefine_REPLICATION_DATA_COMPRESSION_N',
    });

    compressionStatusObj = this.getI18n({
        true: 'common.EnumDefine_CERTIFICATE_STATUS_VALID',
        false: 'common.EnumDefine_CERTIFICATE_STATUS_INVALID',
    });

    initalSyncObj = this.getI18n({
        1: 'common.EnumDefine_STARTUP_MODE_E_AUTO',
        2: 'common.EnumDefine_STARTUP_MODE_E_MANUAL',
    });

    intervalUnitObj = this.getI18n({
        min: 'common.time_minutes_label',
        sec: 'common.time_seconds_label',
    });

    secResDataStatusObj = this.getI18n({
        1: 'common.EnumDefine_RUNNING_STATUS_E_SYNCHRONIZED',
        2: 'common.EnumDefine_HC_DATA_STATE_HC_DATA_INTEGRITY',
        5: 'common.EnumDefine_HC_DATA_STATE_HC_DATA_INCOMPLETENESS',
    });

    secResProtectStatusObj = this.getI18n({
        1: 'protection.replicationCG_view_secondaryLunDataStatus_NoAccess_lab',
        2: 'settings.userAndRole_term_readonly_list',
        3: 'settings.userAndRole_term_readWrite_list',
    });

    bandWidthUnitObj = this.getI18n({
        0: 'KB/s',
        1: 'MB/s',
        2: 'GB/s',
    });

    /**
     * 用户快照同步策略
     */
    syncSnapPolicyOptions: SelectOption[] = [
        {
            label: this.i18n.get('protection.replicaiton_task_createAsync_userSnapSync_no_value'),
            value: SyncSnapPolicyType.NOT_SYNC_SNAP,
        },
        {
            label: this.i18n.get('protection.replicaiton_task_createAsync_userSnapSync_sameAsSource_value'),
            value: SyncSnapPolicyType.SAME_AS_SOURCE,
        },
        {
            label: this.i18n.get('protection.replicaiton_task_createAsync_userSnapSync_retentionNum_value'),
            value: SyncSnapPolicyType.USER_SNAP_RETENTION_NUM,
        },
    ];

    /**
     * 从端副本快照保留策略
     */
    backupCopyPolicyOptions: SelectOption[] = [
        {
            label: this.i18n.get('protection.replicaiton_task_createAsync_secondSnapCopyReten_no_value'),
            value: BackupCopyPolicyType.NO_RETENTION,
        },
        {
            label: this.i18n.get('protection.replicaiton_task_createAsync_secondSnapCopyReten_retentionNum_value'),
            value: BackupCopyPolicyType.COPY_SNAP_RETENTION_NUM,
        },
    ];

    dateOpt: SelectOption[] = [
        {
            label: this.i18nLabel.sun,
            value: 0,
        }, {
            label: this.i18nLabel.mon,
            value: 1,
        }, {
            label: this.i18nLabel.tues,
            value: 2,
        }, {
            label: this.i18nLabel.wed,
            value: 3,
        }, {
            label: this.i18nLabel.thrs,
            value: 4,
        }, {
            label: this.i18nLabel.fri,
            value: 5,
        }, {
            label: this.i18nLabel.sat,
            value: 6,
        },
    ];

    healthStatusOptions = this.obj2Options(this.healthStatusObj);
    runningStatusOptions = this.obj2Options(this.runningStatusObj);
    replicationModeOptions = this.obj2Options(this.replicationModeObj);
    syncMethodOptions = this.obj2Options(this.syncMethodObj);
    roleOptions = this.obj2Options(this.roleObj);
    speedOptions = this.obj2Options(this.speedObj);
    recoverPolicyOptions = this.obj2Options(this.recoverPolicyObj);
    dataTransmisionOptions = this.obj2Options(this.dataTransmisionObj);
    initalSyncOption = this.obj2Options(this.initalSyncObj);
    intervalUnitOptions = this.obj2Options(this.intervalUnitObj);
    secResDataStatusOptions = this.obj2Options(this.secResDataStatusObj);
    bandWidthUnitOptions = this.obj2Options(this.bandWidthUnitObj);

    promptTips = this.getI18n({
        highestSpeedCreate: 'protection.replicationCG_task_modify_speedWithHighest_confirm_msg',
        highestSpeedModify: 'protection.replicationCG_task_create_speedWithHighest_confirm_msg',
        speedGreaterThanOrEqualTo100mCreate: 'protection.replicationCG_task_create_speedGreater_confirm_msg',
        speedGreaterThanOrEqualTo100mModify: 'protection.replicationCG_task_modify_toCustomSpeed_confirm_msg',
        sync: 'protection.replicationCG_task_sync_confirm_msg',
        syncBatch: 'protection.replicationCG_task_batchSyncCG_confirm_msg',
        switch: 'protection.replicationCG_task_switch_confirm_msg',
        split: 'protection.replicationCG_task_split_confirm_msg',
        splitBatch: 'protection.replicationCG_task_batchSplit_confirm_msg',
        delete: 'protection.replication_manageConsistentGroup_task_batch_delete_confirmation_message',
    });

    getPairName(isPrimary: boolean, localName: string, remoteName: string): string {
        return isPrimary ? `${localName} ${this.i18nLabel.pairArrowRight} ${remoteName}` :
            `${remoteName} ${this.i18nLabel.pairArrowRight} ${localName}`;
    }


    poolFreeCapacity(value: string | number): string {
        return this.i18n.get('storage.lun_task_create_availableCapacity_lab', [
            this.util.adaptUnit(value || 0, Consts.SECTOR).label,
        ]);
    }

    nullErrMsg(name: string): string {
        const i18nName = this.i18nLabel[name];
        return this.i18n.get('common.null_validation', [i18nName]);
    }

    /**
     * 长度范围错误
     * @param min
     * @param max
     */
    rangeByteLengthErr(min: number, max: number): string {
        return this.i18n.get('common.length_msg', [min, max]);
    }

    /**
     * 名称格式错误
     * @param name
     */
    commonNameErr(name: string): string {
        const i18nName = this.i18nLabel[name];
        return this.i18n.get('common.nameformat_validation', [i18nName]);
    }

    nameErrMsg(name: string | number): string {
        const i18nName = this.i18nLabel[name];
        return this.i18n.get('common.nameformat_validation', [i18nName]);
    }

    lengthErrMsg(min: number, max: number): string {
        return this.i18n.get('common.length_msg', [min, max]);
    }

    startErrMsg(name: string | number): string {
        const i18nName = this.i18nLabel[name];
        return this.i18n.get('common.name_tip', [i18nName]);
    }

    obj2Options(obj: KV): {
        value: string;
        label: any;
    }[] {
        return Object.keys(obj).map((key) => {
            const option = {
                value: key,
                label: obj[key],
            };
            return option;
        });
    }

    // 转换UTC时间戳为带时区信息的字符串
    renderTime = (time: number): string => time === 0 ?
            UNPLACEHOLDER :
            this.time.formatUTC(time * 1e3);

    private getI18n(kv: KV): KV {
        const keys = Object.keys(kv);
        const result = {};
        keys.forEach((key) => {
            result[key] = this.i18n.get(kv[key]);
        });
        return result;
    }

    decodeStr = (str: string): string => this.util.decodeHTML(str);

    getProgressValue(val): number {
        const num = Number(val);
        return (!isNaN(num) && num <= 100 && num >= 0) ? num : 0;
    }

    getProductModel = (model: string): string => {
        if (!model) {
            return '';
        }

        return this.toolService.getProductModelName(Number(model));
    };

    /**
     * 添加/移除远程复制Pair
     * @param local 本端资源名称
     * @param remote 远端资源名称
     */
    addOrRmovePairToCG(local: string, remote: string, type: string): string {
        return type === 'add' ?
            this.i18n.get('rest.add_remoteRepPair_msg', [local, remote]) :
            this.i18n.get('rest.remove_remoteRepPair_msg', [local, remote]);
    }

    /**
     * 范围错误
     * @param min
     * @param max
     */
    rangeErr(name: string, min: string, max: string): string {
        return this.i18n.get('common.range_validation', [name, min, max]);
    }

    /**
     * 更新速率tip的速率上限
     */
    updateSpeedTipUpLimit(bandwidthUpLimit: number): string {
        return this.i18n.get('protection.replication_create_task_speed_common_tip', [bandwidthUpLimit]);
    }

    /**
     * 更新自定义速率范围的速率上限
     */
    updateRangeLabelUpLimit(bandwidthUpLimit: number): string {
        return this.i18n.get('common.valueRange_label', [BandwidthRange.Min, bandwidthUpLimit]);
    }

    /**
     * @description 带宽，时延，持续 详情页面的展示
     * @param {bandwitdh}  带宽
     * @param {latency} 时延
     * @param {transferCycle} 持续
     */
    getDetailString = (from: 'detail' | 'card', type: 'async' | 'sync', bandwitdh?: string, latency?: number, transferCycle?: number): string => {
        let value = 0;
        let bandwitdhObj: { value: number; unit: string; label: string };
        let str;
        if (bandwitdh) {
            bandwitdhObj = this.randerBandWidth(bandwitdh);
            value += 1;
        }
        if (latency) {
            value += 2;
        }
        if (transferCycle) {
            value += 4;
        }
        const map = new Map([
            [0, (): void => {
                str = UNPLACEHOLDER;
            }],
            [1, (): void => {
                if (from === 'detail') {
                    str = type === 'async' ? this.i18nLabel.asyncRepCondition1Lab(bandwitdhObj.label) :
                        this.i18nLabel.syncRepCondition4Lab(bandwitdhObj.label);
                } else {
                    str = type === 'async' ? this.i18nLabel.cardAsyncRep_condition1Lab(bandwitdhObj.label) :
                        this.i18nLabel.cardSyncRep_condition4Lab(bandwitdhObj.label);
                }
            }],
            [2, (): void => {
                if (from === 'detail') {
                    str = type === 'async' ? this.i18nLabel.asyncRepCondition2Lab(latency) :
                        this.i18nLabel.syncRepCondition5Lab(latency);
                } else {
                    str = type === 'async' ? this.i18nLabel.cardAsyncRep_condition2Lab(latency) :
                        this.i18nLabel.cardSyncRep_condition5Lab(latency);
                }
            }],
            [3, (): void => {
                if (from === 'detail') {
                    str = type === 'async' ?
                        this.i18nLabel.asyncRepCondition3Lab(bandwitdhObj.label, latency) :
                        this.i18nLabel.syncRepCondition6Lab(bandwitdhObj.label, latency);
                } else {
                    str = type === 'async' ?
                        this.i18nLabel.cardAsyncRep_condition3Lab(bandwitdhObj.label, latency) :
                        this.i18nLabel.cardSyncRep_condition6Lab(bandwitdhObj.label, latency);
                }
            }],
            [5, (): void => {
                if (from === 'detail') {
                    str = this.i18nLabel.syncRepCondition1Lab(bandwitdhObj.label, transferCycle);
                } else {
                    str = this.i18nLabel.cardSyncRep_condition1Lab(bandwitdhObj.label, transferCycle);
                }
            }],
            [6, (): void => {
                if (from === 'detail') {
                    str = this.i18nLabel.syncRepCondition2Lab(latency, transferCycle);
                } else {
                    str = this.i18nLabel.cardSyncRep_condition2Lab(latency, transferCycle);
                }
            }],
            [7, (): void => {
                if (from === 'detail') {
                    str = this.i18nLabel.syncRepCondition3Lab(bandwitdhObj.label, latency, transferCycle);
                } else {
                    str = this.i18nLabel.cardSyncRep_condition3Lab(bandwitdhObj.label, latency, transferCycle);
                }
            }],
        ]);
        map.get(value)();
        return str;
    };

    /**
     * 渲染带宽数据
     * @param bandWidth 接口返回的带宽值（KB为单位）
     */
    randerBandWidth(bandWidth: string | number): { value: number; unit: string; label: string } {
        let value;
        let unit;
        if (!bandWidth) {
            return { value, unit, label: UNPLACEHOLDER };
        }

        const k = Number(bandWidth);
        const m = k / 1024;
        const g = m / 1024;

        if (g === Math.floor(g)) {
            value = g;
            unit = 2;
        } else if (m === Math.floor(m)) {
            value = m;
            unit = 1;
        } else {
            value = k;
            unit = 0;
        }

        return {
            value,
            unit,
            label:  value ? value + ' ' + this.bandWidthUnitOptions[unit].label : UNPLACEHOLDER,
        };
    }
}
